import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { AdminIssue } from '@/hooks/useAdminIssues';
import { Lightbulb, Bug, CheckSquare } from 'lucide-react';
import { useErrorLogger } from '@/contexts/ErrorLoggerContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useState } from 'react';
import { captureScreenshot } from '@/utils/captureScreenshot';

interface IssueFormProps {
  onSubmit: (data: Partial<AdminIssue>) => void;
  onCancel: () => void;
  initialData?: AdminIssue;
}

export const IssueForm = ({ onSubmit, onCancel, initialData }: IssueFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getErrorLogs } = useErrorLogger();
  const [isCapturing, setIsCapturing] = useState(false);
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: initialData || {
      type: 'todo' as const,
      priority: 'medium' as const,
    },
  });

  const onFormSubmit = async (data: any) => {
    setIsCapturing(true);
    try {
      // Parse tags if they're a string
      let tags = data.tags;
      if (typeof tags === 'string') {
        tags = tags.split(',').map((tag: string) => tag.trim()).filter(Boolean);
      }

      // Capture screenshot using browser
      let screenshotUrl = null;
      try {
        const screenshotBlob = await captureScreenshot();
        if (screenshotBlob) {
          const fileName = `${user?.id || 'anonymous'}-${Date.now()}.jpg`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('issue-screenshots')
            .upload(fileName, screenshotBlob, {
              contentType: 'image/jpeg',
              cacheControl: '3600',
            });
          
          if (!uploadError && uploadData) {
            const { data: { publicUrl } } = supabase.storage
              .from('issue-screenshots')
              .getPublicUrl(fileName);
            screenshotUrl = publicUrl;
          }
        }
      } catch (err) {
        console.error('Screenshot capture failed:', err);
        // Continue even if screenshot fails
      }

      // Get error logs
      const errorLogs = getErrorLogs();
      const errorLog = errorLogs.length > 0 ? {
        logs: errorLogs,
        capturedAt: new Date().toISOString(),
        totalLogs: errorLogs.length,
      } : null;

      const issueData: Partial<AdminIssue> = {
        title: data.title,
        description: data.description || '',
        type: data.type,
        priority: data.priority,
        tags: tags || [],
        page_url: window.location.href,
        page_title: document.title,
        user_agent: navigator.userAgent,
        created_by: user?.id,
        created_by_email: user?.email || '',
        screenshot_url: screenshotUrl,
        error_log: errorLog,
      };
      
      onSubmit(issueData);
    } catch (err) {
      console.error('Error submitting issue:', err);
      toast.error('Kunne ikke oprette issue');
    } finally {
      setIsCapturing(false);
    }
  };

  const typeValue = watch('type');
  const priorityValue = watch('priority');

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Titel *</Label>
        <Input
          id="title"
          {...register('title', { required: true })}
          placeholder="Titel på issue..."
        />
        {errors.title && <p className="text-sm text-destructive">Titel er påkrævet</p>}
      </div>

      <div className="space-y-2">
        <Label>Type *</Label>
        <RadioGroup value={typeValue} onValueChange={(value) => setValue('type', value as any)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="idea" id="idea" />
            <Label htmlFor="idea" className="flex items-center gap-2 cursor-pointer">
              <Lightbulb className="h-4 w-4 text-primary" />
              💡 Idé
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="bug" id="bug" />
            <Label htmlFor="bug" className="flex items-center gap-2 cursor-pointer">
              <Bug className="h-4 w-4 text-destructive" />
              🐛 Bug
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="todo" id="todo" />
            <Label htmlFor="todo" className="flex items-center gap-2 cursor-pointer">
              <CheckSquare className="h-4 w-4 text-accent" />
              ✅ To-do
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="priority">Prioritet</Label>
        <Select value={priorityValue} onValueChange={(value) => setValue('priority', value as any)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Lav</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">Høj</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Beskrivelse</Label>
        <Textarea
          id="description"
          {...register('description')}
          placeholder="Beskriv issue i detaljer..."
          rows={5}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Tags</Label>
        <Input
          id="tags"
          {...register('tags')}
          placeholder="tag1, tag2, tag3"
        />
        <p className="text-sm text-muted-foreground">Adskil tags med komma</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isCapturing}>
          Annuller
        </Button>
        <Button type="submit" disabled={isCapturing}>
          {isCapturing ? 'Capturing...' : (initialData ? 'Opdater' : 'Opret')} Issue
        </Button>
      </div>
    </form>
  );
};
